import { produce } from 'immer';
import { merge, pick } from 'lodash';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';

import {
  IState,
  IStatePatchArrayItemOptions,
  IStatePatchArrayItemsOptions,
  StateAccessor,
  StateData,
} from './data-store.types';
import { snapshot } from './data-store.utils';

//

export class StateUpdateError extends Error {}
export class StateUpdateTypeError extends TypeError {}

export class State<D extends StateData> implements IState<D> {
  protected readonly _$ :BehaviorSubject<D>;
  readonly $ :Observable<D>;
  readonly KEYS :ReadonlyArray<keyof D>;

  constructor(initialState :D) {
    this._$ = new BehaviorSubject<D>(initialState);
    this.$ = this._$.asObservable();

    this.KEYS = Object.freeze(Object.keys(initialState)) as ReadonlyArray<keyof D>;
  }

  getSnapshot() :D {
    return snapshot(this._getValue());
  } // return a deep snapshot, to ensure things don't change

  observe<K extends keyof D>(accessor :K) :Observable<D[K]>;
  observe<K extends keyof D>(accessor :K[]) :Observable<Pick<D, K>>;
  observe<T>(accessor :StateAccessor<D>) :Observable<T>;
  observe(accessor ?:null | undefined) :Observable<D>;
  observe<T, K extends keyof D>(
    accessor ?:K | K[] | StateAccessor<D> | null | undefined,
  ) :Observable<D[K]> | Observable<Pick<D, K>> | Observable<T> | Observable<D> {
    return accessor == null
      ? this.$
      : this.$.pipe(
          map(
            typeof accessor === 'function'
              ? accessor // accessor is of type `StateAccessor<D>`
              : (state :D) :Pick<D, K> | D[K] =>
                  Array.isArray(accessor)
                    ? (pick(state, accessor) as Pick<D, K>) // accessor type is `K[]`
                    : (state[accessor] as D[K]), // accessor type is `K`
          ),
          distinctUntilChanged(),
        );
  }

  /**
   * Patch an item in an array.  By default it will compare the `id` property of
   * the `value` argument with the `id` properties of existing records in the
   * array.
   *
   * @param {string} property
   *   A key in the state object corresponding to an array value.
   * @param {*} value
   *   An update state of a record in the array.
   * @param {IStatePatchArrayItemOptions} [options]
   * @param {function(candidate, value):boolean}
   *   [options.comparator=((candidate, value) => candidate.id === value.id)]
   *   Comparator to use to determine whether a candidate matches the provided
   *   value.  The 'value' argument will be passed in as it is received by this
   *   method.  The 'candidate' argument will be an existing item in the array.
   * @param {boolean} [options.skipMissing=false]
   *   Determine how to respond if the array doesn't have a matching value in it.
   *   Set to `false` (or omit) to add the item to the array; set to `true` to
   *   do nothing, leaving the array unchanged.
   */
  patchArrayItem<
    // K is any property name in the state data
    K extends keyof D,
    // I is the type of the items in the array; in other words D[K] === I[]
    I extends D[K] extends Array<infer U> ? U : never,
  >(property :K, value :I, options ?:IStatePatchArrayItemOptions<I>) :void {
    this.patchArrayItems(property, [value], options);
  }
  /**
   * Patch an item in an array.  By default it will compare the `id` property of
   * the `value` argument with the `id` properties of existing records in the
   * array.
   *
   * @param {string} property
   *   A key in the state object corresponding to an array value.
   * @param {*[]} values
   *   Updated states of records in the array.
   * @param {IStatePatchArrayItemOptions} [options]
   * @param {function(candidate, value):boolean}
   *   [options.comparator=((candidate, value) => candidate.id === value.id)]
   *   Comparator to use to determine whether a candidate matches the provided
   *   value.  The 'value' argument will be passed in as it is received by this
   *   method.  The 'candidate' argument will be an existing item in the array.
   * @param {boolean} [options.skipMissing=false]
   *   Determine how to respond if the array doesn't have a matching value in it.
   *   Set to `false` (or omit) to add the item to the array; set to `true` to
   *   do nothing, leaving the array unchanged.
   */
  patchArrayItems<
    // K is any property name in the state data
    K extends keyof D,
    // I is the type of the items in the array; in other words D[K] === I[]
    I extends D[K] extends Array<infer U> ? U : never,
  >(property :K, values :I[], options ?:IStatePatchArrayItemsOptions<I>) :void {
    const state :D = this._getValue();

    // protect state
    if (!Array.isArray(state[property])) {
      throw new StateUpdateTypeError(
        `Cannot update item of non-array property '${String(property)}'`,
      );
    }

    // extract options
    options = {
      // DEFAULTS
      comparator: (candidate :any, value :any) => candidate.id === value.id,
      skipMissing: false,
      merge: false,

      ...options,
    };

    // exit early, 'cause we won't find an existing item in an empty array
    if (options.skipMissing && (state[property] as I[]).length < 1) {
      return;
    }

    // do it
    this._$.next(
      produce(state, (draft) => {
        const array = (draft as D)[property] as I[];

        values.forEach((value :I) => {
          let index;

          try {
            index = array.findIndex((candidate) =>
              options.comparator(candidate, value),
            );
          } catch (err) {
            throw new StateUpdateError(err.message);
          }

          if (index in array) {
            // FYI: this returns `false` when `index === -1`
            if (
              options.merge &&
              typeof array[index] === 'object' &&
              typeof value === 'object'
            ) {
              if (options.merge === 'deep') {
                merge(array[index], value);
              } else {
                Object.assign(array[index], value);
              }
            } else {
              array[index] = value;
            }
          } else if (!options.skipMissing) {
            array.push(value);
          }
        });
      }),
    );
  }

  toJSON() :D {
    return this.getSnapshot();
  }

  update(updates :Partial<D>) :void {
    const state :D = this._getValue();

    this._$.next(produce(state, (draft) => ({ ...draft, ...updates })));
  }

  protected _getValue() :D {
    return this._$.getValue();
  }
}
